import React, { useEffect, useRef, useState } from 'react';
import { api, input, labelCls } from '../ui/Kit';

// Part E: one "Search address" box for offices — debounced, keyboard-navigable (↑ ↓ Enter Esc), 5 suggestions from the
// server (Azure Maps behind our own endpoints; the key never reaches the browser). Picking one resolves it to the
// street, city, state, postcode, country, coordinates and time zone. When the search isn't available (no key, or the
// provider fails) the parent is told, quietly, and shows the manual pickers instead — never an error wall.
export function AddressSearch({ onPick, onUnavailable, autoFocus }) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const seq = useRef(0);
  useEffect(() => {
    const text = q.trim();
    if (text.length < 3) { setItems([]); setOpen(false); return undefined; }
    const mine = ++seq.current;
    const t = setTimeout(async () => {
      try {
        const r = await api(`/api/geo/address/suggest?q=${encodeURIComponent(text)}`);
        if (mine !== seq.current) return;
        if (r.available === false) { onUnavailable && onUnavailable(); return; }
        setItems(r.suggestions || []); setActive(-1); setOpen(true); setNote((r.suggestions || []).length ? '' : 'No matching address — try more of the street, or enter it manually.');
      } catch (e) {
        if (mine === seq.current) onUnavailable && onUnavailable();
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = async (s) => {
    setOpen(false); setBusy(true); setNote('');
    try {
      const r = await api(`/api/geo/address/resolve?id=${encodeURIComponent(s.id)}`);
      setQ(''); setItems([]);
      onPick(r);
    } catch (e) { setNote(e.message); }
    setBusy(false);
  };
  const key = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((a) => Math.min(items.length - 1, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === 'Enter' && open && items[active]) { e.preventDefault(); choose(items[active]); }
    else if (e.key === 'Escape') setOpen(false);
  };
  return (
    <div className="relative" data-testid="address-search">
      <label className="block"><span className={labelCls}>Search address</span>
        <input className={input} role="combobox" aria-expanded={open} aria-controls="address-suggestions" aria-autocomplete="list"
          aria-activedescendant={active >= 0 ? `address-opt-${active}` : undefined} autoComplete="off" autoFocus={autoFocus}
          placeholder="Start typing the street address…" value={q} disabled={busy} onChange={(e) => setQ(e.target.value)} onKeyDown={key}
          data-testid="address-input" /></label>
      {open && items.length > 0 && (
        <ul id="address-suggestions" role="listbox" className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#E6E9F0] rounded-lg shadow-lg max-h-64 overflow-auto" data-testid="address-suggestions">
          {items.map((s, i) => (
            <li key={s.id} id={`address-opt-${i}`} role="option" aria-selected={i === active} onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setActive(i)} data-testid="address-option"
              className={`px-3 py-2 text-sm cursor-pointer ${i === active ? 'bg-[#E8F1FB] text-[#003D82]' : 'text-[#334155]'}`}>{s.label}</li>))}
        </ul>)}
      {note && <p className="text-xs text-[#64748B] mt-1" role="status" data-testid="address-note">{note}</p>}
    </div>
  );
}

// 'America/New_York · Eastern Time' — the long generic name from the browser's zone data; the IANA name alone if it has none
export function zoneLabel(zone) {
  if (!zone) return '';
  for (const style of ['longGeneric', 'long']) {
    try {
      const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: style }).formatToParts(new Date()).find((p) => p.type === 'timeZoneName');
      if (part && part.value) return `${zone} · ${part.value}`;
    } catch (e) { /* try the next style */ }
  }
  return zone;
}
